import { EMR_APIS } from "@/api_framework/api_config";
import axios, { AxiosError, AxiosResponse } from "axios";
import { useCallback } from "react";

import AxiosCurlirize from "axios-curlirize";
import { useAPIUtils } from "../../useAPIUtils";
import { useConfiguration } from "../../useConfiguration";
import { useCurlLogger } from "../../useCurlLogger";
import { IVoiceEMRItems, IVoiceEMRText } from "./useVoiceEMRResultAPI";

AxiosCurlirize(axios, () => {});

interface IVoiceEMRTextResponse {
  voice_id: string;
  status: string;
  emr_text: IVoiceEMRText;
}

interface IVoiceEMRItemsResponse {
  voice_id: string;
  status: string;
  emr_items: IVoiceEMRItems;
}

/**
 * Generates the EMR for a finished recording.
 *
 * Two independent halves, meant to be fired in parallel. `text` comes back first
 * in the common case — it does no catalog lookups — so the written EMR can render
 * while item searches are still running.
 *
 * Both are idempotent: a half that already has results returns them without
 * re-running the models, so retrying just the failed half is cheap and safe.
 */
export const useVoiceEMRGenerateAPI = () => {
  const configuration = useConfiguration();
  const { captureError } = useAPIUtils();
  const { sendCurl } = useCurlLogger();

  const post = useCallback(
    async <T>(voiceId: string, half: "text" | "items"): Promise<T | null> => {
      try {
        const accessToken = localStorage.getItem(configuration.localStorage.ACCESS_TOKEN.key);
        const url = `${EMR_APIS?.VOICE_EMR_GENERATE?.baseURL}/${voiceId}/generate/${half}`;

        return await axios
          .post(
            url,
            {},
            {
              headers: {
                Authorization: `${accessToken}`,
                "Content-Type": "application/json",
              },
            },
          )
          .then((res: AxiosResponse<{ data?: T }>) => res.data?.data ?? null)
          .catch((e: AxiosError) => {
            captureError(e);
            sendCurl(e.config?.["curlCommand"]);
            return null;
          });
      } catch {
        return null;
      }
    },
    [configuration, captureError, sendCurl],
  );

  const generateText = useCallback(
    async (voiceId: string) => (await post<IVoiceEMRTextResponse>(voiceId, "text"))?.emr_text ?? null,
    [post],
  );

  const generateItems = useCallback(
    async (voiceId: string) => (await post<IVoiceEMRItemsResponse>(voiceId, "items"))?.emr_items ?? null,
    [post],
  );

  return { generateText, generateItems };
};
